// Catalog.ID Client — Settings Pane
// ============================================================================
// Account settings: identity info, emergency lockdown, identity linking, credentials,
// recovery kit, devices and sessions.

import { createElement, createFieldGroup, createFormRow, renderFormField, renderPaneActions } from '@catalog-shell';
import type { PaneType, PaneTypeRenderContext, FormRendererResult } from '@catalog-shell';
import * as accountApi from '../api/account';
import * as delegateApi from '../api/delegate';
import { formatApiError } from '../api/client';
import { liveAgentsMessage, withAgentAcknowledgement } from '../auth/principal-termination';
import { computeAuthHash } from '../crypto/credentials';
import { session } from '../state/session';
import { identity } from '../state/identity';
import { keys } from '../state/keys';
import { signStepUp } from '../auth/step-up';
import { nodeHostFor } from '../state/origins';
import { forgetThisDevice, serverRecordsLeftMessage } from '../auth/device-session';
import { buildSessionDeps } from '../auth/logout-action';
import type { AccountInfo } from '../api/types';
import { PANE_IDS } from '../shell-config';
import { downloadBlob, downloadPdf, printPdf } from '../components/pdf-loader';
import { buildCredentialsPage } from '../components/booklet/credentials-page';
import { buildBookletDefinition } from '../components/booklet/booklet';
import { buildResetCardPage } from '../components/booklet/reset-card-page';
import { formatVersion } from '../components/booklet/shared';
import { createSupersecretTextarea } from '../components/supersecret-textarea';
import { decryptKeyBag } from '../crypto/keybag';
import { exportKeyBagBundle, serializeBundle, bundleFilename } from '../crypto/bundle';
import { collectBlobsForBundle } from '../crypto/bundle-blobs';
import * as blobApi from '../api/blobs';
import {
    showRecoveryKitModal as showRecoveryKitCreatorModal,
    showRecoveryReprintModal,
    showRecoveryDrillModal,
} from '../components/recovery-kit-modals';
import { persistPersonalKitEntry } from '../components/recovery-kit-store';
import type { RecoveryKitSource } from '../components/recovery-kit-creator';
import { appendRecoveryKitStatus } from '../components/recovery-kit-status';
import { planRecoveryKitActions } from '../components/recovery-kit-actions';
import { recordExportedBundle } from '../components/recovery-kit-record';
import { appendAuthenticatorKeyPanel } from '../components/authenticator-key-panel';
import {
    authenticatorStatusLine, describeAuthenticatorError, disableAuthenticator, enableAuthenticator,
    issueAuthenticatorKey, revealAuthenticator,
} from '../auth/authenticator';
import * as claimsApi from '../api/claims';
import * as aes from '../crypto/aes';
import { reEncryptClaimKey, reEncryptClaimKeyWithChain } from '../crypto/claims';
import { reconstructSupersecret } from '../crypto/recovery-kit';
import { parseBundle, decryptKeyBagBundle, type KeyBagBundle } from '../crypto/bundle';
import { appendWalletsGroup } from './bitcash-wallets-group';

const TYPE_LABELS: Record<string, string> = {
    natural_person: 'Personal',
    organization: 'Organization',
    delegate: 'Delegate',
    agent: 'Agent',
};

export function createSettingsPane(): PaneType {
    return {
        typeId: PANE_IDS.settings,
        kind: 'leaf',
        defaultTitle: 'Settings',
        capabilities: [],
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            void renderSettings(container, ctx);
            return container;
        },
    };
}

async function renderSettings(container: HTMLElement, ctx: PaneTypeRenderContext): Promise<void> {
    const active = identity.getActive();

    // Fetch account info from server
    let accountInfo: AccountInfo | null = null;
    try {
        accountInfo = await accountApi.getAccountInfo();
    } catch {
        // Fall back to session state
    }

    // Account info
    const infoGroup = createFieldGroup({ title: 'Account Information' });

    if (active) {
        const cidRow = createFormRow({ label: 'Catalog.ID' });
        cidRow.content.appendChild(createElement('span', {
            className: 'cid-profile-cid',
            textContent: active.cid,
        }));
        infoGroup.body.appendChild(cidRow.element);

        const typeRow = createFormRow({ label: 'Identity Type' });
        typeRow.content.appendChild(createElement('span', {
            textContent: TYPE_LABELS[accountInfo?.accountType ?? active.accountType] ?? active.accountType,
        }));
        infoGroup.body.appendChild(typeRow.element);

        const statusRow = createFormRow({ label: 'Status' });
        statusRow.content.appendChild(createElement('span', {
            textContent: accountInfo?.status ?? session.getInfo()?.status ?? '',
        }));
        infoGroup.body.appendChild(statusRow.element);

        if (accountInfo?.creationTime) {
            const dateRow = createFormRow({ label: 'Created' });
            dateRow.content.appendChild(createElement('span', {
                textContent: new Date(accountInfo.creationTime).toLocaleDateString(),
            }));
            infoGroup.body.appendChild(dateRow.element);
        }

        if (accountInfo?.isOperator || session.isOperator()) {
            const opRow = createFormRow({ label: 'Operator' });
            opRow.content.appendChild(createElement('span', { textContent: 'Yes' }));
            infoGroup.body.appendChild(opRow.element);
        }
    }
    container.appendChild(infoGroup.element);

    // Identity document reprint
    if (active) {
        const docGroup = createFieldGroup({ title: 'Identity Document' });
        docGroup.body.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'Print or download a copy of your credentials document. Your password and '
                + 'supersecret are not stored anywhere and appear only in the document set from your '
                + 'original registration.',
        }));
        const activeCid = active.cid;
        const accountTypeLabel = TYPE_LABELS[accountInfo?.accountType ?? active.accountType] ?? active.accountType;
        // No-secrets reprint variant of the credentials page
        // (registration-restructure M3) — a one-page document set, same layout
        // family as the original registration print.
        const reprintPages = () => [buildCredentialsPage({
            header: { cid: activeCid, accountTypeLabel, version: formatVersion(new Date()) },
        })];
        const docActions = createElement('div', { className: 'cid-credential-actions' });
        const docError = createElement('div', { className: 'cid-pane-error' });
        const docButton = (label: string, run: () => Promise<void>): HTMLElement => {
            const btn = createElement('button', {
                className: 'cs-btn cs-btn--default cid-credential-action',
                textContent: label,
            });
            btn.addEventListener('click', async () => {
                btn.setAttribute('disabled', '');
                docError.classList.remove('cid-pane-error--visible');
                try {
                    await run();
                } catch (err) {
                    docError.textContent = err instanceof Error ? err.message : `${label} failed`;
                    docError.classList.add('cid-pane-error--visible');
                } finally {
                    btn.removeAttribute('disabled');
                }
            });
            return btn;
        };
        docActions.appendChild(docButton('Print', () => printPdf(buildBookletDefinition(reprintPages()))));
        docActions.appendChild(docButton('Download PDF',
            () => downloadPdf(buildBookletDefinition(reprintPages()), `catalog-id-${activeCid}.pdf`)));
        docGroup.body.appendChild(docActions);
        docGroup.body.appendChild(docError);
        container.appendChild(docGroup.element);

        // Encrypted key backup (bundle) export — Recovery Kit Addendum §2.4/§5.
        const backupGroup = createFieldGroup({ title: 'Encrypted Key Backup' });
        backupGroup.body.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: 'Export your key bag as an encrypted backup file. It is ciphertext that only your '
                + 'supersecret can open — safe to store anywhere, and the more copies survive, the better: '
                + 'your devices, cloud storage, USB drives, or people you trust. Export a fresh backup '
                + 'after adding or rotating keys; older backups stay usable as snapshots.',
        }));
        const exportBtn = createElement('button', {
            className: 'cs-btn cs-btn--default',
            textContent: 'Export encrypted key backup…',
        });
        exportBtn.addEventListener('click', () => showBundleExportModal(ctx, activeCid));
        backupGroup.body.appendChild(exportBtn);
        container.appendChild(backupGroup.element);

        // Recovery Kit (Recovery Kit Addendum §7/§12/§14)
        container.appendChild(await renderRecoveryKitGroup(ctx, activeCid,
            (accountInfo?.accountType ?? active.accountType) === 'organization', accountTypeLabel));

        // Wallets (registration-wallets §7): the bag's bitcash wallets, their
        // receive addresses and the spending permissions services hold on them.
        // Session-scoped like everything else in this pane, which is what makes
        // it serve an ORGANIZATION account over the organization's own bag —
        // and the only place the organization's receive address can be
        // published, publication being `session.AccountId`-scoped server-side.
        // Not offered to agent accounts: an agent has no wallet of its own, and
        // the group's first act would be a supersecret prompt for an empty list.
        if ((accountInfo?.accountType ?? active.accountType) !== 'agent') {
            appendWalletsGroup(container, ctx, activeCid);
        }

        // Account Unlock Sheet (password-reset plan §5.4.3, renamed §12) —
        // personal identities AND, since round 6 (§13.3), delegates: every
        // delegate now verifies an email and a phone claim, which is exactly
        // what the sheet is sealed against. Organizations never get one — the
        // organization account has no user-facing password and no channels of
        // its own.
        const cardAccountType = accountInfo?.accountType ?? active.accountType;
        if (cardAccountType === 'natural_person' || cardAccountType === 'delegate') {
            // The header the reissued sheet prints — the same rows the original
            // kit carried, so a delegate's reissue is indistinguishable from the
            // sheet in their Delegate Membership Kit.
            const cardHeader = {
                cid: activeCid,
                accountTypeLabel,
                orgCid: accountInfo?.organizationCid ?? undefined,
                orgRole: accountInfo?.roles?.[0] ?? undefined,
            };
            // A stable host so the group can be re-rendered in place, both after a
            // reissue and reactively when a bound email/phone claim changes (M6):
            // the server bumps the epoch, so the sheet's staleness banner must
            // refresh without a manual reload. One subscription, cleaned up with
            // the pane via ctx.disposables.
            const resetCardHost = createElement('div');
            const refreshResetCard = async (justIssued?: IssuedAccessCard) => {
                resetCardHost.replaceChildren(
                    await renderResetCardGroup(ctx, activeCid, cardHeader, refreshResetCard, justIssued ?? null));
            };
            await refreshResetCard();
            container.appendChild(resetCardHost);
            ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
                if ((data as { type?: string })?.type === 'claims') void refreshResetCard();
            }));

            // Authenticator (registration-restructure §15.6). Same audience as
            // the Unlock Sheet — personal identities and delegates — and, unlike
            // Emergency Lockdown, deliberately visible to a `partial` delegate:
            // the activation wizard stores the key under that status, so the
            // member must be able to turn the code on afterwards.
            const authenticatorHost = createElement('div');
            const refreshAuthenticator = async (): Promise<void> => {
                authenticatorHost.replaceChildren(
                    await renderAuthenticatorGroup(ctx, activeCid, cardHeader, refreshAuthenticator));
            };
            await refreshAuthenticator();
            container.appendChild(authenticatorHost);
        }
    }

    // Session — Forget this device only. Plain "Log Out" is a MODULE verb and
    // lives in the gear menu (`cid.log-out`, register-pane-types.ts); repeating
    // it here is what made the two sit side by side, one of them destructive.
    // Forget de-enrols every identity as well as ending its session, which
    // costs the supersecret on the next login — hence the confirm gate.
    const sessionGroup = createFieldGroup({ title: 'Session' });

    // The world both flows act on, built once in auth/logout-action.ts so this
    // pane and the gear-menu Log Out cannot clear different things.
    const sessionDeps = buildSessionDeps();

    const forgetBtn = createElement('button', {
        className: 'cs-btn cs-btn--no',
        textContent: 'Forget this device',
    });
    sessionGroup.body.appendChild(forgetBtn);
    sessionGroup.body.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Logs out and removes this device from every identity that uses it. The next '
            + 'login here will need the supersecret.',
    }));

    forgetBtn.addEventListener('click', async () => {
        const ok = await ctx.shell.confirm({
            title: 'Forget this device',
            message: 'This logs you out and removes this device from every identity that uses it. '
                + 'The next login on this device will need the supersecret for every identity — '
                + 'your password alone will not be enough. Continue?',
            confirmLabel: 'Forget this device',
            confirmVariant: 'no',
            requireExplicitChoice: true,
        });
        if (!ok) return;
        forgetBtn.setAttribute('disabled', '');
        forgetBtn.textContent = 'Forgetting...';
        const result = await forgetThisDevice(sessionDeps);
        const leftover = serverRecordsLeftMessage(result.serverRecordsLeft);
        if (leftover) {
            // The user needs this before the reload takes the page away.
            ctx.shell.toast(leftover, 'warning');
            await new Promise(resolve => setTimeout(resolve, 4000));
        }
        window.location.href = '/';
    });

    container.appendChild(sessionGroup.element);

    // Emergency lockdown. Closed server-side to a `partial` account (an invited
    // delegate whose organization keys an owner has not wrapped yet), so the
    // action is hidden rather than offered and then refused (§13.3, M13).
    const accountStatus = accountInfo?.status ?? session.getInfo()?.status;
    if (accountStatus !== 'partial') {
        const lockdownGroup = createFieldGroup({ title: 'Emergency Lockdown' });
        // docs/cid-governance.md M15: a lockdown PAUSES the account's agents and
        // the unlock brings them back — they are no longer terminated.
        lockdownGroup.body.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'Immediately lock this account. All sessions are revoked and all devices locked. '
                + 'Your agents are paused: their access is revoked and services refuse them until you '
                + 'unlock, and then they come back. Your Account Unlock Sheet together with a code to your '
                + 'email or phone unlocks the account again; without the sheet, an operator review is needed.',
        }));

        const lockdownBtn = createElement('button', {
            className: 'cs-btn cs-btn--no',
            textContent: 'Emergency Lockdown',
        });
        lockdownBtn.addEventListener('click', async () => {
            const ok = await ctx.shell.confirm({
                title: 'Emergency Lockdown',
                message: 'This will immediately lock your account. All sessions will be revoked and all '
                    + 'devices locked. Your agents are paused while the account is locked and come back when '
                    + 'you unlock it; issue them new API keys then. Your Account Unlock Sheet together '
                    + 'with a code to your email or phone unlocks the account again; without the sheet, an '
                    + 'operator review is needed. Are you absolutely sure?',
                confirmLabel: 'Lock My Account',
                confirmVariant: 'no',
                requireExplicitChoice: true,
            });
            if (!ok) return;

            lockdownBtn.setAttribute('disabled', '');
            lockdownBtn.textContent = 'Locking...';
            try {
                await accountApi.lockdown();
                ctx.shell.toast('Account locked. All sessions revoked.', 'warning');
                // Clear client state and redirect to login
                session.clear();
                window.location.href = '/';
            } catch (err) {
                lockdownBtn.removeAttribute('disabled');
                lockdownBtn.textContent = 'Emergency Lockdown';
                ctx.shell.toast(formatApiError(err, 'Lockdown failed'), 'error');
            }
        });
        lockdownGroup.body.appendChild(lockdownBtn);
        container.appendChild(lockdownGroup.element);
    }

    // Account termination
    const terminateGroup = createFieldGroup({ title: 'Terminate Account' });
    terminateGroup.body.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Permanently terminate this account. All encrypted claims, keys, devices, and certificates will be destroyed. Your username will be permanently retired and never reassigned.',
    }));

    const eraseRow = createFormRow({ label: 'Public claims' });
    const eraseLabel = createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' } });
    const eraseCheckbox = document.createElement('input');
    eraseCheckbox.type = 'checkbox';
    eraseLabel.appendChild(eraseCheckbox);
    eraseLabel.appendChild(createElement('span', { textContent: 'Also delete my public claims (full GDPR erasure)' }));
    eraseRow.content.appendChild(eraseLabel);
    terminateGroup.body.appendChild(eraseRow.element);
    terminateGroup.body.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        style: { opacity: '0.7', fontSize: '0.85em', marginTop: '4px' },
        textContent: 'By default, published public claims are preserved under the perpetual publication license.',
    }));

    const confirmField = renderFormField(
        { type: 'text', key: 'confirm', label: 'Type your CID to confirm', placeholder: active?.cid ?? '' },
        '', () => {},
    );
    const confirmRow = createFormRow({ label: 'Confirm CID' });
    confirmRow.content.appendChild(confirmField.element);
    terminateGroup.body.appendChild(confirmRow.element);

    const terminateBtn = createElement('button', {
        className: 'cs-btn cs-btn--no',
        textContent: 'Terminate Account',
    });
    terminateBtn.addEventListener('click', async () => {
        const confirmValue = String(confirmField.getValue()).trim();
        if (confirmValue !== active?.cid) {
            ctx.shell.toast('CID does not match. Please type your exact CID to confirm.', 'error');
            return;
        }

        const ok = await ctx.shell.confirm({
            title: 'Terminate Account',
            message: 'This action is irreversible. All encrypted data will be permanently destroyed. Are you absolutely sure?',
            confirmLabel: 'Terminate My Account',
            confirmVariant: 'no',
            requireExplicitChoice: true,
        });
        if (!ok) return;

        terminateBtn.setAttribute('disabled', '');
        terminateBtn.textContent = 'Terminating...';
        try {
            const erasePublicClaims = eraseCheckbox.checked;
            // Step-up: terminate is irreversible, so the server requires a signature
            // from main_signing_key (proof of key possession), not just the session token.
            // The account's live agents end with it, for good, only once the member
            // agrees (docs/cid-governance.md M15).
            const done = await withAgentAcknowledgement(async (terminateAgents) => {
                const challengeSignature = await signStepUp('terminate');
                await accountApi.terminate({ erasePublicClaims, challengeSignature, terminateAgents });
            }, (agents) => ctx.shell.confirm({
                title: 'Terminate Your Agents',
                message: liveAgentsMessage(agents, 'This account is'),
                confirmLabel: 'Terminate Account and Agents',
                confirmVariant: 'no',
                requireExplicitChoice: true,
            }));
            if (!done) {
                terminateBtn.removeAttribute('disabled');
                terminateBtn.textContent = 'Terminate Account';
                return;
            }
            ctx.shell.toast('Account terminated. All data destroyed.', 'warning');
            session.clear();
            window.location.href = '/';
        } catch (err) {
            terminateBtn.removeAttribute('disabled');
            terminateBtn.textContent = 'Terminate Account';
            ctx.shell.toast(formatApiError(err, 'Termination failed'), 'error');
        }
    });
    terminateGroup.body.appendChild(terminateBtn);
    container.appendChild(terminateGroup.element);

    // Identity linking (personal identities only) — deferred from Phase 8e
    if (identity.isPersonal()) {
        const linkGroup = createFieldGroup({ title: 'Linked Identities' });

        // Show currently linked identities
        try {
            const linked = await delegateApi.getLinkedIdentities();
            const delegates = linked.filter(id => id.accountType === 'delegate');

            if (delegates.length > 0) {
                for (const del of delegates) {
                    const row = createElement('div', { className: 'cid-share-item' });
                    row.appendChild(createElement('span', {
                        className: 'cid-share-cid',
                        textContent: `${del.cid} (${TYPE_LABELS[del.accountType] ?? del.accountType})`,
                    }));
                    const unlinkBtn = createElement('button', {
                        className: 'cs-btn cs-btn--no cs-btn--small',
                        textContent: 'Unlink',
                    });
                    unlinkBtn.addEventListener('click', async () => {
                        const ok = await ctx.shell.confirm({
                            title: 'Unlink Identity',
                            message: `Remove the link to ${del.cid}? You can re-link later with the delegate's credentials.`,
                            confirmLabel: 'Unlink',
                            confirmVariant: 'no',
                        });
                        if (!ok) return;
                        try {
                            await delegateApi.unlinkDelegate({ delegateCid: del.cid });
                            ctx.shell.toast(`Unlinked ${del.cid}`, 'success');
                            row.remove();
                        } catch (err) {
                            ctx.shell.toast(err instanceof Error ? err.message : 'Unlink failed', 'error');
                        }
                    });
                    row.appendChild(unlinkBtn);
                    linkGroup.body.appendChild(row);
                }
            } else {
                linkGroup.body.appendChild(createElement('p', {
                    className: 'cid-wizard-text',
                    textContent: 'No linked delegate identities.',
                }));
            }
        } catch {
            linkGroup.body.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: 'No linked delegate identities.',
            }));
        }

        // Link new delegate
        const linkBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cs-btn--small',
            textContent: '+ Link Delegate Identity',
        });
        linkBtn.addEventListener('click', () => {
            void showLinkDialog(ctx);
        });
        linkGroup.body.appendChild(linkBtn);
        container.appendChild(linkGroup.element);
    }

    // Domain revalidation used to have a second home here, offered to ANY
    // delegate with an organization. The server takes it only from an owner or
    // an admin (OrganizationController.RevalidateDomain →
    // ResolveOrgAndCheckRole(session, orgCid, "owner", "admin")), so for a
    // viewer or a treasurer this was a button that could only 403 — and every
    // caller the server does allow already reaches the organization workspace's
    // copy under the org-claims ⋯ menu (org-workspace-actions.ts, same
    // owner/admin gate; org-claims is on every delegate's org roster). Removed
    // 2026-09-10; the org menu is the single copy.
}

async function showLinkDialog(ctx: PaneTypeRenderContext): Promise<void> {
    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: 'Link Delegate Identity',
    }));
    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Enter the delegate identity\'s CID and password to create a link for context switching.',
    }));

    const cidField = renderFormField(
        { type: 'text', key: 'cid', label: 'Delegate CID', placeholder: 'e.g., ab12cd34fg' },
        '', () => {},
    );
    const cidRow = createFormRow({ label: 'CID' });
    cidRow.content.appendChild(cidField.element);
    content.appendChild(cidRow.element);

    let passField: FormRendererResult;
    passField = renderFormField(
        { type: 'text', key: 'pass', label: 'Password', placeholder: 'Delegate password' },
        '', () => {},
    );
    const passRow = createFormRow({ label: 'Password' });
    passRow.content.appendChild(passField.element);
    content.appendChild(passRow.element);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(errorEl);

    const submitBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Link',
    });
    content.appendChild(submitBtn);

    const modal = ctx.shell.showModal(content);

    submitBtn.addEventListener('click', async () => {
        const delegateCid = String(cidField.getValue()).trim();
        const password = String(passField.getValue());
        if (!delegateCid || !password) {
            errorEl.textContent = 'Both fields are required';
            errorEl.classList.add('cid-pane-error--visible');
            return;
        }

        errorEl.classList.remove('cid-pane-error--visible');
        submitBtn.setAttribute('disabled', '');
        submitBtn.textContent = 'Linking...';

        try {
            const personalCid = identity.getActive()?.cid;
            if (!personalCid) throw new Error('No identity is signed in');
            const authHash = await computeAuthHash(password, delegateCid);
            // The password goes only to the delegate's own node, which vouches for
            // it; the link is then written here, at the personal identity's node
            // (docs/cid-governance.md M15a).
            await delegateApi.linkDelegateIdentity({ personalCid, delegateCid, delegateAuthHash: authHash });
            modal.close();
            ctx.shell.toast(`Linked to ${delegateCid}`, 'success');
            // Reload to refresh identity picker
            window.location.reload();
        } catch (err) {
            submitBtn.removeAttribute('disabled');
            submitBtn.textContent = 'Link';
            errorEl.textContent = formatApiError(err, 'Linking failed');
            errorEl.classList.add('cid-pane-error--visible');
        }
    });
}

// ============================================================================
// Encrypted key backup (bundle) export
// ============================================================================

/**
 * Prompt for the supersecret, verify it by decrypting the server key bag
 * (AES-GCM tag check — catches wrong supersecrets cleanly, M12 re-prompt
 * pattern), then export the bag as an encrypted bundle under a fresh salt
 * and download it.
 */
function showBundleExportModal(ctx: PaneTypeRenderContext, cid: string): void {
    const content = createElement('div', { className: 'cid-pane-content', style: { padding: '20px' } });

    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: 'Export Encrypted Key Backup',
    }));
    content.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Enter your supersecret. It is verified against your key bag, used locally to encrypt '
            + 'the backup under a fresh salt, then discarded. The resulting file can only be opened with '
            + 'your supersecret.',
    }));

    const ss = createSupersecretTextarea({ autoFocus: true });
    content.appendChild(ss.element);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(errorEl);

    const exportBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Export',
    }) as HTMLButtonElement;
    exportBtn.disabled = true;
    ss.onValidityChange(valid => { exportBtn.disabled = !valid; });
    content.appendChild(exportBtn);

    const modal = ctx.shell.showModal(content, { size: 'wide' });

    exportBtn.addEventListener('click', async () => {
        if (!ss.isValid()) return;
        const supersecret = ss.getValue();

        errorEl.classList.remove('cid-pane-error--visible');
        exportBtn.setAttribute('disabled', '');
        exportBtn.textContent = 'Verifying supersecret...';

        try {
            const kbResp = await accountApi.getKeyBag();
            let bag;
            try {
                bag = await decryptKeyBag(kbResp, supersecret);
            } catch {
                throw new Error('Invalid supersecret — could not decrypt key bag');
            }

            exportBtn.textContent = 'Encrypting backup...';
            const publicKeys = await accountApi.getOwnPublicKeys();
            // The account's blobs ride along as-is (cid-encrypted-storage
            // §4.2): the bundle already carries `derive_root`, so the same
            // supersecret that opens the bag re-derives every blob key. A
            // failure here is FATAL for this export — the member asked for a
            // backup, and a backup that silently omits their application state
            // is worse than no file at all.
            const blobs = await collectBlobsForBundle(blobApi);
            const bundle = await exportKeyBagBundle({
                cid,
                bag,
                supersecret,
                publicKeys,
                createdAt: new Date().toISOString(),
                blobs,
            });

            downloadBlob(
                new Blob([serializeBundle(bundle)], { type: 'application/json' }),
                bundleFilename(cid, bundle),
            );
            modal.close();
            ctx.shell.toast('Encrypted key backup exported', 'success');

            // §16.7 / M25: the kit record names the NEWEST bundle, and the fire
            // drill compares the file the member brings against it. Recording
            // this export is what keeps a perfectly good backup from being
            // announced as "NOT the latest bundle on record". Best-effort: the
            // file is already on disk, so nothing here may throw or block.
            await recordExportedBundle({ fingerprint: bundle.bundle_fingerprint }, {
                getKit: accountApi.getRecoveryKit,
                updateBundle: accountApi.updateRecoveryKitBundle,
                report: (m) => ctx.shell.toast(m, 'warning'),
            });
        } catch (err) {
            exportBtn.removeAttribute('disabled');
            exportBtn.textContent = 'Export';
            errorEl.textContent = err instanceof Error ? err.message : 'Export failed';
            errorEl.classList.add('cid-pane-error--visible');
        }
    });
}

// ============================================================================
// Account Unlock Sheet (password-reset plan §5.4.3; renamed by
// registration-restructure §12 — the sheet unlocks the account as well as
// resetting the password)
// ============================================================================

async function renderResetCardGroup(
    ctx: PaneTypeRenderContext,
    cid: string,
    /** Account rows the printed sheet carries — 'Personal', or a delegate's
     *  Organization / Role (round 6, §13.3). */
    header: { cid: string; accountTypeLabel: string; orgCid?: string; orgRole?: string },
    refresh: (justIssued?: IssuedAccessCard) => Promise<void>,
    /** Set right after an issue so the sheet can be printed from a FRESH user
     *  gesture — `printPdf` opens a window, and popup activation does not
     *  survive the issue call's awaits (plan §11). */
    justIssued: IssuedAccessCard | null = null,
): Promise<HTMLElement> {
    const group = createFieldGroup({ title: 'Account Unlock Sheet' });

    group.body.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'A printable sheet that restores your login: it unlocks the account after a lock '
            + 'and can set a new password, using a code sent to your verified email or phone — without '
            + 'your supersecret. Every use needs that code, so the sheet alone gives nobody control. '
            + 'The sheet is sealed by your Operator\'s validator: it does not reveal your email or '
            + 'phone, and it cannot open your keys or data. It stays valid after it is used and after '
            + 'a lock — only the newest sheet works, so issuing a new one permanently invalidates the '
            + 'previous. Issue a new one if you suspect the printout was copied. Using the sheet can '
            + 'also issue a new Authenticator Key, which is the way back in when the phone AND the '
            + 'Authenticator Key sheet are gone.',
    }));

    const statusEl = createElement('p', { className: 'cid-pane-note' });
    group.body.appendChild(statusEl);

    let card: accountApi.ResetCardInfo | null = null;
    try {
        card = await accountApi.getResetCard();
    } catch {
        // Status stays generic; issuing still works.
    }

    if (card) {
        const created = new Date(card.createdAt).toLocaleDateString();
        statusEl.textContent = `Sheet issued ${created} (checksum ${card.tokenFingerprint.slice(0, 8)}…).`;

        if (card.epoch < card.currentEpoch) {
            // The account epoch moved past the sheet's — a channel change or
            // revocation superseded it. The sheet is dead; only a reissue helps.
            group.body.appendChild(createElement('p', {
                className: 'cid-wizard-warning',
                textContent: 'This sheet is NO LONGER VALID — it was superseded by a later change '
                    + '(reissue, channel update, or revocation). Issue a new sheet and destroy the old printout.',
            }));
        } else if (Date.now() - new Date(card.createdAt).getTime() > 365 * 24 * 3600 * 1000) {
            group.body.appendChild(createElement('p', {
                className: 'cid-wizard-warning',
                textContent: 'This sheet is over 12 months old. Verify the printout is still readable, and '
                    + 'that the email and phone it was issued against are still yours.',
            }));
        }
    } else {
        statusEl.textContent = 'No Account Unlock Sheet on record for this identity.';
    }

    const actions = createElement('div', { className: 'cid-credential-actions' });
    const issueBtn = createElement('button', {
        className: 'cs-btn cs-btn--default',
        textContent: card ? 'Issue New Unlock Sheet…' : 'Create Unlock Sheet…',
    });
    issueBtn.addEventListener('click', async () => {
        issueBtn.setAttribute('disabled', '');
        try {
            const issued = await issueResetCardFromSettings(ctx, cid, Boolean(card));
            if (issued) {
                // Re-render the group in place so status/staleness reflect the new
                // sheet. Goes through the shared refresh so the host stays the
                // single owner of the current group node — and carries the freshly
                // sealed token so the print button below can appear.
                await refresh(issued);
                return;
            }
        } finally {
            issueBtn.removeAttribute('disabled');
        }
    });
    actions.appendChild(issueBtn);
    group.body.appendChild(actions);

    if (justIssued) {
        group.body.appendChild(createElement('p', {
            className: 'cid-wizard-warning',
            textContent: 'Your new Account Unlock Sheet is ready — print it now. It is not stored '
                + 'anywhere in printable form: leaving this screen without printing means issuing '
                + 'another sheet.',
        }));

        // One page, the same builder and letterhead the registration document
        // set uses, so a sheet issued here is indistinguishable from the printed
        // original. Both outputs go through pdf-loader.
        const cardPages = () => [buildResetCardPage({
            header: { ...header, version: formatVersion(justIssued.createdAt) },
            sealedToken: justIssued.sealedToken,
            // The account's origin, which printed sheets name (M7a).
            nodeHost: nodeHostFor(cid),
        })];

        const cardActions = createElement('div', { className: 'cid-credential-actions' });
        const cardError = createElement('div', { className: 'cid-pane-error' });
        const cardButton = (label: string, run: () => Promise<void>): HTMLElement => {
            const btn = createElement('button', {
                className: 'cs-btn cs-btn--yes cid-credential-action',
                textContent: label,
            });
            btn.addEventListener('click', async () => {
                btn.setAttribute('disabled', '');
                cardError.classList.remove('cid-pane-error--visible');
                try {
                    await run();
                } catch (err) {
                    cardError.textContent = err instanceof Error ? err.message : `${label} failed`;
                    cardError.classList.add('cid-pane-error--visible');
                } finally {
                    btn.removeAttribute('disabled');
                }
            });
            return btn;
        };
        cardActions.appendChild(cardButton('Print Account Unlock Sheet',
            () => printPdf(buildBookletDefinition(cardPages()))));
        cardActions.appendChild(cardButton('Download PDF',
            () => downloadPdf(buildBookletDefinition(cardPages()), `catalog-id-unlock-sheet-${cid}.pdf`)));
        group.body.appendChild(cardActions);
        group.body.appendChild(cardError);
    }

    return group.element;
}

/**
 * The Authenticator group (registration-restructure §15.6): status, and the
 * four things a member can do with a printed Authenticator Key.
 *
 *   Turn on       reveal (step-up) → QR on screen → code → enable
 *   Turn off      code + step-up
 *   Show key      reveal → QR, for a new phone
 *   Issue new key step-up → 20 CSPRNG bytes → PUT → QR + print sheet
 *
 * The asymmetry is the server's and is deliberate (§15.8): a live code is what
 * turns the gate on, a code AND key custody is what turns it off, and key
 * custody alone is what reveals or replaces the secret.
 */
async function renderAuthenticatorGroup(
    ctx: PaneTypeRenderContext,
    cid: string,
    /** The rows a printed replacement sheet carries — 'Personal', or a
     *  delegate's Organization / Role. */
    header: { cid: string; accountTypeLabel: string; orgCid?: string; orgRole?: string },
    refresh: () => Promise<void>,
): Promise<HTMLElement> {
    const group = createFieldGroup({ title: 'Authenticator' });

    group.body.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'A login code from an authenticator app, on top of your password. The key it '
            + 'reads is printed on the Authenticator Key sheet in your kit and stored on this node; '
            + 'turning the code on or off never changes the key. If you lose the phone AND the '
            + 'sheet, your Account Unlock Sheet issues a new key.',
    }));

    let info: AccountInfo | null = null;
    try {
        info = await accountApi.getAccountInfo();
    } catch { /* status stays generic; the actions still work */ }

    const enabled = info?.totpEnabled === true;
    const hasKey = info?.totpKeyIssued !== false;
    group.body.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: hasKey
            ? authenticatorStatusLine({ enabled, confirmedAt: info?.totpConfirmedAt })
            : 'No Authenticator Key on record for this identity — issue one below to use a login code.',
    }));

    const error = createElement('div', { className: 'cid-pane-error' });
    const panelHost = createElement('div');
    const actions = createElement('div', { className: 'cid-credential-actions' });

    const fail = (err: unknown): void => {
        error.textContent = describeAuthenticatorError(err);
        error.classList.add('cid-pane-error--visible');
    };
    const action = (label: string, run: () => Promise<void>): HTMLElement => {
        const btn = createElement('button', { className: 'cs-btn cs-btn--default', textContent: label });
        btn.addEventListener('click', async () => {
            btn.setAttribute('disabled', '');
            error.classList.remove('cid-pane-error--visible');
            try {
                await run();
            } catch (err) {
                fail(err);
            } finally {
                btn.removeAttribute('disabled');
            }
        });
        return btn;
    };

    /** QR + characters, plus a six-digit field that turns the gate on. */
    const showKeyPanel = (
        secret: Uint8Array, uri: string | undefined,
        opts: { intro: string; offerEnable: boolean; print: boolean },
    ): void => {
        panelHost.replaceChildren();
        appendAuthenticatorKeyPanel(panelHost, {
            cid, secret, uri, intro: opts.intro, header, print: opts.print,
        });
        if (!opts.offerEnable) return;

        const field = createElement('div', { className: 'cid-auth-field' });
        const codeInput = document.createElement('input');
        codeInput.type = 'text';
        codeInput.className = 'cid-auth-input';
        codeInput.placeholder = '000000';
        codeInput.inputMode = 'numeric';
        codeInput.maxLength = 6;
        codeInput.autocomplete = 'one-time-code';
        field.appendChild(codeInput);
        panelHost.appendChild(field);
        panelHost.appendChild(action('Turn on the login code', async () => {
            await enableAuthenticator(codeInput.value);
            ctx.shell.toast('Login code is on.', 'success');
            await refresh();
        }));
    };

    if (hasKey && !enabled) {
        actions.appendChild(action('Turn on…', async () => {
            const revealed = await revealAuthenticator();
            showKeyPanel(revealed.secret, revealed.uri, {
                intro: 'Scan this with your authenticator app, then type the six digits it shows.',
                offerEnable: true,
                print: false,
            });
        }));
    }

    if (hasKey && enabled) {
        const offField = createElement('div', { className: 'cid-auth-field' });
        const offInput = document.createElement('input');
        offInput.type = 'text';
        offInput.className = 'cid-auth-input';
        offInput.placeholder = 'Current code, to turn it off';
        offInput.inputMode = 'numeric';
        offInput.maxLength = 6;
        offInput.autocomplete = 'one-time-code';
        offField.appendChild(offInput);
        group.body.appendChild(offField);

        actions.appendChild(action('Turn off', async () => {
            await disableAuthenticator(offInput.value);
            ctx.shell.toast('Login code is off.', 'success');
            await refresh();
        }));
    }

    if (hasKey) {
        actions.appendChild(action('Show key…', async () => {
            const revealed = await revealAuthenticator();
            showKeyPanel(revealed.secret, revealed.uri, {
                intro: 'Scan this on a new phone. The key is unchanged — every device that already '
                    + 'has it keeps working.',
                offerEnable: false,
                print: true,
            });
        }));
    }

    actions.appendChild(action(hasKey ? 'Issue a new key…' : 'Issue a key…', async () => {
        if (hasKey) {
            const ok = await ctx.shell.confirm({
                title: 'Issue a new Authenticator Key',
                message: 'This replaces the key on the Authenticator Key sheet in your kit. That sheet '
                    + 'stops working, every authenticator app holding the old key stops producing valid '
                    + 'codes, and your login code stays OFF until you scan the new key and confirm it. '
                    + 'Print the replacement sheet before you leave this screen. Continue?',
                confirmLabel: 'Issue a new key',
                confirmVariant: 'no',
                requireExplicitChoice: true,
            });
            if (!ok) return;
        }
        const secret = await issueAuthenticatorKey();
        // Rendered right here, from a value that exists only in this page: the
        // node will not hand it back without a step-up, so print it now.
        showKeyPanel(secret, undefined, {
            intro: 'Your new Authenticator Key. Scan it with your app and print the replacement sheet '
                + 'for your kit — the old sheet is void. The login code is off until you confirm the '
                + 'new key below.',
            offerEnable: true,
            print: true,
        });
    }));

    group.body.appendChild(actions);
    group.body.appendChild(error);
    group.body.appendChild(panelHost);
    return group.element;
}

/** A freshly sealed Account Unlock Sheet, held only long enough to print it. */
interface IssuedAccessCard {
    sealedToken: Uint8Array;
    createdAt: Date;
}

/** Issue a new Account Unlock Sheet from settings: pick the validated
 *  email+phone claims, re-wrap their keys for the validator, seal.
 *  Returns the sealed sheet when one was issued, else null. */
async function issueResetCardFromSettings(
    ctx: PaneTypeRenderContext,
    cid: string,
    hasExisting: boolean,
): Promise<IssuedAccessCard | null> {
    const claims = await claimsApi.listClaims();
    // Open decision 2 (plural claims): v1 binds the first validated instance of
    // each type — the pair validated at registration for a typical account.
    const emailClaim = claims.find(c =>
        c.claimType === 'email' && c.validationStatus === 'validated' && !c.revokedAt);
    const phoneClaim = claims.find(c =>
        c.claimType === 'phone' && c.validationStatus === 'validated' && !c.revokedAt);
    if (!emailClaim || !phoneClaim) {
        ctx.shell.toast('An Account Unlock Sheet needs a validated email AND a validated phone claim. '
            + 'Validate both under Claims first.', 'warning');
        return null;
    }

    const attrSecretKeyB64 = keys.getPrivateKey('attr_encrypt');
    if (!attrSecretKeyB64) {
        ctx.shell.toast('Decryption key not available on this device', 'error');
        return null;
    }

    if (hasExisting) {
        const ok = await ctx.shell.confirm({
            title: 'Issue New Unlock Sheet',
            message: 'Issuing a new Account Unlock Sheet permanently invalidates your existing sheet. '
                + 'Destroy the old printout after printing the new one. Continue?',
            confirmLabel: 'Issue New Sheet',
        });
        if (!ok) return null;
    }

    try {
        // Re-encrypt each claim's symmetric key for the validator (§4.3) — the
        // same materials pattern as claim validation.
        const { publicKey: validatorKeyB64 } = await claimsApi.getValidatorKey();
        const validatorPublicKey = aes.fromBase64(validatorKeyB64);
        const rewrapSelfKey = async (claimUuid: string): Promise<string> => {
            const detail = await claimsApi.getClaim(claimUuid);
            const selfKey = detail.keys.find(k => k.recipientType === 'self');
            if (!selfKey) throw new Error('No encryption key found for the claim');
            if (selfKey.keyIndex > 0) {
                // Post-rotation: chain-walk to unwrap, then re-wrap for the validator
                return reEncryptClaimKeyWithChain(
                    selfKey.encryptedKey, keys.getKeyChain('attr_encrypt'), selfKey.keyIndex, validatorPublicKey);
            }
            return reEncryptClaimKey(
                selfKey.encryptedKey, aes.fromBase64(attrSecretKeyB64), validatorPublicKey);
        };

        const result = await accountApi.issueResetCard({
            emailClaimUuid: emailClaim.claimUuid,
            emailEncryptedKey: await rewrapSelfKey(emailClaim.claimUuid),
            phoneClaimUuid: phoneClaim.claimUuid,
            phoneEncryptedKey: await rewrapSelfKey(phoneClaim.claimUuid),
        });
        ctx.shell.toast('Account Unlock Sheet issued — print it and keep it somewhere safe.', 'success');
        return {
            sealedToken: aes.fromBase64(result.sealedToken),
            createdAt: new Date(result.createdAt),
        };
    } catch (err) {
        ctx.shell.toast(formatApiError(err, 'Failed to issue the Account Unlock Sheet'), 'error');
        return null;
    }
}

// ============================================================================
// Recovery Kit (Recovery Kit Addendum §7/§12/§14)
// ============================================================================

async function renderRecoveryKitGroup(
    ctx: PaneTypeRenderContext,
    cid: string,
    isOrganization: boolean,
    /** Account-header line on the printed recovery sheets. */
    accountTypeLabel: string,
): Promise<HTMLElement> {
    const group = createFieldGroup({ title: 'Recovery Kit' });

    group.body.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'A Recovery Kit splits your supersecret across printable sheets using threshold '
            + 'secret sharing: any quorum of distinct sheets reconstructs it, fewer reveal nothing. '
            + 'Together with an encrypted key backup it makes your identity recoverable even if '
            + 'every device, password manager, and Catalog.ID node is gone. Any sheet of your set can be '
            + 'printed again at any time, so a lost sheet can be replaced and a new custodian can be given '
            + 'one without disturbing the sheets already handed out.',
    }));

    let kit: accountApi.RecoveryKitInfo | null = null;
    try {
        kit = await accountApi.getRecoveryKit();
    } catch {
        // Status stays generic; creation still works.
    }

    appendRecoveryKitStatus(group.body, kit, 'No Recovery Kit on record for this identity.');

    // The same two ceremonies the organization's Recovery Kit pane offers, from
    // the same plan (components/recovery-kit-actions.ts) so the labels cannot
    // drift. They stay in the BODY here rather than in the title row: Settings
    // has no single subject — this group sits among credentials, devices,
    // sessions and the authenticator key — so a title-row button would claim to
    // be about the whole pane.
    const kitOnRecord = kit;
    const actions = createElement('div', { className: 'cid-credential-actions' });
    // `medium`: these render into the group BODY, among this pane's regular
    // buttons — `small` is the title-row size and would read as a different
    // class of control here.
    for (const el of renderPaneActions(planRecoveryKitActions(kitOnRecord, (action) => {
        if (action === 'create') {
            showRecoveryKitModal(ctx, cid, isOrganization, Boolean(kitOnRecord), accountTypeLabel);
        } else if (action === 'reprint' && kitOnRecord) {
            showReprintModal(ctx, cid, kitOnRecord, accountTypeLabel);
        } else if (action === 'test' && kitOnRecord) {
            showFireDrillModal(ctx, cid, kitOnRecord);
        }
    }), { size: 'medium' })) {
        actions.appendChild(el);
    }
    group.body.appendChild(actions);

    return group.element;
}

/**
 * Fire drill (§14): scanning each sheet is the single-sheet integrity test
 * (14.2 — MAC/set checks happen on decode); adding a bundle file is the
 * availability test (14.1 — fingerprint compared against the kit record);
 * the quorum test (14.3) reconstructs the supersecret in memory, decrypts
 * every bundle entry, and records last-tested on success.
 */
function showFireDrillModal(ctx: PaneTypeRenderContext, cid: string, kit: accountApi.RecoveryKitInfo): void {
    let bundle: KeyBagBundle | null = null;

    showRecoveryDrillModal({
        shell: ctx.shell,
        intro: `Kit ${kit.recoverySetId.slice(0, 8)}… (${kit.thresholdK} of ${kit.totalSharesN}). `
            + `Scan ${kit.thresholdK} distinct sheets and provide an encrypted key backup file. The test `
            + 'reconstructs your supersecret in memory only, decrypts every backup entry, and records the '
            + 'result — nothing is stored or sent anywhere.',

        // §14.1 — bundle availability.
        extras: (host, onReadinessChange) => {
            const bundleStatus = createElement('p', { className: 'cid-pane-note', textContent: 'No backup file provided yet.' });
            const bundleInput = document.createElement('input');
            bundleInput.type = 'file';
            bundleInput.accept = 'application/json,.json';
            bundleInput.id = 'cid-firedrill-bundle';
            bundleInput.style.display = 'none';
            bundleInput.addEventListener('change', async () => {
                const file = bundleInput.files?.[0];
                if (!file) return;
                try {
                    bundle = parseBundle(await file.text());
                    bundleStatus.textContent = bundle.bundle_fingerprint === kit.bundleFingerprint
                        ? `Backup file OK — matches the kit's latest bundle (${bundle.bundle_fingerprint.slice(0, 8)}…).`
                        : `Backup file readable, but it is NOT the latest bundle on record — it is an older or `
                            + `foreign snapshot (${bundle.bundle_fingerprint.slice(0, 8)}… vs `
                            + `${kit.bundleFingerprint.slice(0, 8)}…). It can still be tested.`;
                } catch (err) {
                    bundle = null;
                    bundleStatus.textContent = err instanceof Error ? err.message : 'Could not read the backup file';
                }
                onReadinessChange();
            });
            const bundleBtn = createElement('button', {
                className: 'cs-btn cs-btn--default cid-credential-action',
                textContent: 'Add encrypted key backup file…',
            });
            bundleBtn.addEventListener('click', () => bundleInput.click());
            const bundleRow = createElement('div', { className: 'cid-credential-actions' });
            bundleRow.appendChild(bundleBtn);
            host.appendChild(bundleRow);
            host.appendChild(bundleInput);
            host.appendChild(bundleStatus);

            return { isReady: () => bundle !== null };
        },

        run: async (cards) => {
            const supersecret = reconstructSupersecret(cards);
            const { bag } = await decryptKeyBagBundle(bundle!, supersecret, cid);

            try {
                await accountApi.markRecoveryKitTested();
            } catch { /* result stands even if the timestamp write fails */ }

            return `Recovery test PASSED — supersecret reconstructed from `
                + `${cards.length} sheets and all ${bag.entries.length} backup entries decrypted. `
                + 'Recorded as tested today.';
        },
    });
}

/** The SESSION account's own bag and published keys, verified by the typed
 *  supersecret. Shared by create/reissue and by the reprint, so the two
 *  ceremonies cannot disagree about what "your key bag" means. */
function sessionKitSource(): RecoveryKitSource {
    return {
        needsSupersecretInput: true,
        resolve: async (supersecretInput) => {
            if (!supersecretInput) throw new Error('Enter your supersecret');
            const kbResp = await accountApi.getKeyBag();
            let bag;
            try {
                bag = await decryptKeyBag(kbResp, supersecretInput);
            } catch {
                throw new Error('Invalid supersecret — could not decrypt key bag');
            }
            const publicKeys = await accountApi.getOwnPublicKeys();
            return { supersecret: supersecretInput, bag, publicKeys };
        },
    };
}

function showRecoveryKitModal(
    ctx: PaneTypeRenderContext,
    cid: string,
    isOrganization: boolean,
    isReissue: boolean,
    accountTypeLabel: string,
): void {
    showRecoveryKitCreatorModal({
        shell: ctx.shell,
        isReissue,
        creator: {
            cid,
            accountTypeLabel,
            kind: isOrganization ? 'organization' : 'personal',
            source: sessionKitSource(),
            // §16.6: the set is stored in this account's own bag through the
            // single-entry add, then read back before anything is printed.
            persistKitEntry: persistPersonalKitEntry,
            // The kit's bundle carries the account's blobs (§4.2). This is the
            // SESSION account's own kit, so the session-scoped blob endpoints
            // answer for the right account.
            collectBlobs: () => collectBlobsForBundle(blobApi),
        },
    });
}

/** Print more sheets of the set the key bag already holds (§16.6). */
function showReprintModal(
    ctx: PaneTypeRenderContext,
    cid: string,
    kit: accountApi.RecoveryKitInfo,
    accountTypeLabel: string,
): void {
    showRecoveryReprintModal({
        shell: ctx.shell,
        reprint: {
            cid,
            accountTypeLabel,
            source: sessionKitSource(),
            record: kit,
            // A record that already names this set is refreshed through the
            // bundle endpoint, never re-PUT: a PUT resets the fire-drill date
            // (§16.9).
            refreshBundle: accountApi.updateRecoveryKitBundle,
            recordKit: accountApi.putRecoveryKit,
            collectBlobs: () => collectBlobsForBundle(blobApi),
        },
    });
}
